// ============================================
// PAGE - Boletas (Detalle Boleta)
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdPrint, MdEdit, MdDelete, MdFileDownload, MdWarning, MdRefresh, MdCalendarToday } from 'react-icons/md';
import { useBoletasConFiltros, useEliminarBoleta, useUIStore, getFechaHoyLocal, formatFechaCL, formatearAntiguedad } from '@almacenes/core';
import type { Boleta } from '@almacenes/core';
import { usePrintBoleta } from '../../hooks/usePrintBoleta';

interface DeleteConfirmation {
  boleta: Boleta;
}

const SS_KEY_DESDE = 'boletas_fecha_desde';
const SS_KEY_HASTA = 'boletas_fecha_hasta';
const SS_KEY_GUARDADO = 'boletas_fecha_guardado';

// Cooldown del botón "Actualizar": evita ráfagas de GET /ventas por clic repetido.
const COOLDOWN_REFRESCAR_MS = 5_000;

// Calcular fechas iniciales: por defecto, el día en curso.
// Si hay un rango guardado en sessionStorage de la misma sesión de hoy, se restaura
// (para no perder el filtro al volver del detalle de una boleta); si el rango quedó
// de un día anterior (sesión larga que cruzó medianoche), se descarta.
const getInitialDates = () => {
  const savedDesde = sessionStorage.getItem(SS_KEY_DESDE);
  const savedHasta = sessionStorage.getItem(SS_KEY_HASTA);
  const savedEl = sessionStorage.getItem(SS_KEY_GUARDADO);
  const hoy = getFechaHoyLocal();

  if (savedDesde && savedHasta && savedEl === hoy) {
    return {
      desde: savedDesde,
      hasta: savedHasta,
    };
  }

  return {
    desde: hoy,
    hasta: hoy,
  };
};

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

// Describe el rango de fechas activo en lenguaje natural para el badge "Estás viendo...".
const describirRango = (desde: string, hasta: string): string => {
  const hoy = getFechaHoyLocal();

  if (desde === hasta) {
    return desde === hoy ? 'el día de hoy' : `el día ${formatFechaCL(desde)}`;
  }

  const [yDesde, mDesde, dDesde] = desde.split('-').map(Number);
  const [yHasta, mHasta, dHasta] = hasta.split('-').map(Number);
  const ultimoDiaMes = new Date(yHasta, mHasta, 0).getDate();

  const esMesCalendarioCompleto =
    yDesde === yHasta && mDesde === mHasta && dDesde === 1 && dHasta === ultimoDiaMes;

  if (esMesCalendarioCompleto) {
    return `el mes de ${MESES[mDesde - 1]} ${yDesde}`;
  }

  return `el período del ${formatFechaCL(desde)} al ${formatFechaCL(hasta)}`;
};

// El estado por defecto del módulo: rango acotado al día de hoy.
const esRangoPorDefecto = (desde: string, hasta: string): boolean =>
  desde === hasta && desde === getFechaHoyLocal();

export const BoletasPage = () => {
  const navigate = useNavigate();

  const [fechaDesde, setFechaDesde] = useState(() => getInitialDates().desde);
  const [fechaHasta, setFechaHasta] = useState(() => getInitialDates().hasta);

  // Estado para controlar si se debe hacer la búsqueda
  const [shouldSearch, setShouldSearch] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation | null>(null);

  // Cooldown del botón "Actualizar" (timestamp hasta el que queda bloqueado)
  const [bloqueadoHasta, setBloqueadoHasta] = useState(0);
  // Fuerza un re-render periódico para que la leyenda de antigüedad y la cuenta
  // regresiva del cooldown avancen solas, sin depender de que cambien otros datos.
  const [, forceTick] = useState(0);

  // Hooks
  const { printBoleta, loadingPrintId } = usePrintBoleta();

  // Hook de React Query
  const { data: boletas, isLoading, error, dataUpdatedAt, isFetching, refetch } = useBoletasConFiltros(
    { desde: fechaDesde, hasta: fechaHasta },
    shouldSearch && fechaDesde !== '' && fechaHasta !== ''
  );

  const eliminarBoletaMutation = useEliminarBoleta();
  const addToast = useUIStore((state) => state.addToast);

  // Guardar fechas en sessionStorage cuando cambian
  useEffect(() => {
    if (fechaDesde && fechaHasta) {
      sessionStorage.setItem(SS_KEY_DESDE, fechaDesde);
      sessionStorage.setItem(SS_KEY_HASTA, fechaHasta);
      sessionStorage.setItem(SS_KEY_GUARDADO, getFechaHoyLocal());
      setShouldSearch(true);
    }
  }, [fechaDesde, fechaHasta]);

  // Leyenda "actualizado hace X" viva: se refresca cada 30s.
  useEffect(() => {
    const interval = setInterval(() => forceTick((n) => n + 1), 30_000);
    return () => clearInterval(interval);
  }, []);

  const segundosRestantes = Math.max(0, Math.ceil((bloqueadoHasta - Date.now()) / 1000));
  const refrescarDeshabilitado = isFetching || segundosRestantes > 0;

  // Cuenta regresiva del cooldown: solo corre mientras hay un bloqueo activo.
  useEffect(() => {
    if (segundosRestantes <= 0) return;
    const interval = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(interval);
  }, [segundosRestantes > 0]);

  const handleRefrescar = () => {
    // La guarda vive acá, no solo en el atributo `disabled` del botón: evita
    // disparos si se salta esa defensa de UI y evita dobles clics entre renders.
    if (refrescarDeshabilitado) return;
    setBloqueadoHasta(Date.now() + COOLDOWN_REFRESCAR_MS);
    refetch();
  };

  // Formatear fecha para mostrar (DD-MM-YYYY HH:mm)
  const formatFechaHora = (fecha: string) => {
    const date = new Date(fecha);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Calcular totales
  const calcularTotales = () => {
    if (!boletas || boletas.length === 0) {
      return { valorNeto: 0, iva: 0, total: 0 };
    }

    const total = boletas.reduce((sum, boleta) => sum + boleta.total, 0);
    const valorNeto = Math.round(total / 1.19);
    const iva = total - valorNeto;

    return { valorNeto, iva, total };
  };

  const totales = calcularTotales();

  // Funciones para eliminar boleta
  const handleDeleteClick = (boleta: Boleta) => {
    setDeleteConfirmation({ boleta });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmation) return;

    try {
      await eliminarBoletaMutation.mutateAsync(deleteConfirmation.boleta.id);
      setDeleteConfirmation(null);
    } catch (error) {
      console.error('Error al eliminar boleta:', error);
      addToast('error', (error as { message?: string })?.message ?? 'No se pudo anular la boleta');
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation(null);
  };

  const handleDownloadExcel = async () => {
    if (!boletas || boletas.length === 0) return;

    try {
      setIsDownloadingExcel(true);

      const { Workbook } = await import('exceljs');
      const wb = new Workbook();
      wb.creator = 'Almacenes Digitales';
      wb.created = new Date();

      const C = {
        dark:    'FF11194F',
        brand:   'FF43BFFF',
        bgLight: 'FFF3F4F9',
        white:   'FFFFFFFF',
        green:   'FF22C55E',
        border:  'FFE5E7EB',
        blue:    'FFE0F2FE',
      };
      const CLP = '#,##0';
      const N = 6;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const styleTitle = (row: any) => {
        row.height = 34;
        row.font = { name: 'Calibri', bold: true, color: { argb: C.white }, size: 13 };
        row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.brand } };
        row.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
        for (let i = 1; i <= N; i++) row.getCell(i).border = {};
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const styleHeader = (row: any) => {
        row.height = 26;
        row.font = { name: 'Calibri', bold: true, color: { argb: C.white }, size: 10 };
        row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.dark } };
        row.alignment = { horizontal: 'center', vertical: 'middle' };
        for (let i = 1; i <= N; i++) {
          row.getCell(i).border = {
            top:    { style: 'medium', color: { argb: C.brand } },
            bottom: { style: 'medium', color: { argb: C.brand } },
            left:   { style: 'thin',   color: { argb: C.dark } },
            right:  { style: 'thin',   color: { argb: C.dark } },
          };
        }
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const styleData = (row: any, isAlt: boolean) => {
        row.height = 20;
        if (isAlt) row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.bgLight } };
        for (let i = 1; i <= N; i++) {
          row.getCell(i).border = {
            bottom: { style: 'thin', color: { argb: C.border } },
            left:   { style: 'thin', color: { argb: C.border } },
            right:  { style: 'thin', color: { argb: C.border } },
          };
        }
      };

      const ws = wb.addWorksheet('Boletas');
      ws.columns = [
        { key: 'voucher',   width: 15 },
        { key: 'sii',       width: 15 },
        { key: 'fecha',     width: 22 },
        { key: 'vendedor',  width: 28 },
        { key: 'tipoPago',  width: 16 },
        { key: 'total',     width: 16 },
      ];

      const formatDateForTitle = (d: string) => d.split('-').reverse().join('/');
      const tRow = ws.addRow([`Boletas ${formatDateForTitle(fechaDesde)} – ${formatDateForTitle(fechaHasta)}`, '', '', '', '', '']);
      ws.mergeCells('A1:F1');
      styleTitle(tRow);

      const hRow = ws.addRow(['Voucher', 'Boleta SII', 'Fecha-Hora', 'Vendedor', 'Tipo Pago', 'Total ($)']);
      styleHeader(hRow);

      boletas.forEach((boleta, idx) => {
        const row = ws.addRow([
          !boleta.dteEmitido ? boleta.id : '-',
          boleta.dteEmitido && boleta.siiDteEmitidoFolio ? boleta.siiDteEmitidoFolio : '-',
          formatFechaHora(boleta.fecha),
          boleta.vendedor,
          boleta.tipoPago,
          boleta.total,
        ]);
        styleData(row, idx % 2 !== 0);
        row.getCell(6).numFmt = CLP;
        row.getCell(6).alignment = { horizontal: 'right' };
      });

      ws.addRow([]);

      const makeResumenRow = (label: string, value: number, bgArgb: string, fontArgb: string) => {
        const row = ws.addRow(['', '', '', '', label, value]);
        row.height = 24;
        row.font = { name: 'Calibri', bold: true, color: { argb: fontArgb }, size: 11 };
        row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgArgb } };
        row.getCell(5).alignment = { horizontal: 'right' };
        row.getCell(6).numFmt = CLP;
        row.getCell(6).alignment = { horizontal: 'right' };
        for (let i = 5; i <= 6; i++) {
          row.getCell(i).border = {
            top:    { style: 'medium', color: { argb: C.brand } },
            bottom: { style: 'medium', color: { argb: C.brand } },
            left:   { style: 'thin',   color: { argb: C.border } },
            right:  { style: 'thin',   color: { argb: C.border } },
          };
        }
      };

      makeResumenRow('Valor Neto', totales.valorNeto, C.bgLight, C.dark);
      makeResumenRow('IVA (19%)', totales.iva, C.bgLight, C.dark);
      makeResumenRow('TOTAL', totales.total, C.green, C.white);

      const formatDateForFilename = (d: string) => d.split('-').reverse().join('-');
      const filename = `boletas_${formatDateForFilename(fechaDesde)}_${formatDateForFilename(fechaHasta)}.xlsx`;

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al generar Excel:', error);
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  return (
    <div className="px-4 md:px-6 lg:px-8 pb-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold" style={{ color: '#11194F' }}>
          Detalle Boleta
        </h1>
        <p className="text-gray-600 mt-1">
          Consulta y gestiona el historial de boletas de venta
        </p>
      </div>

      {/* Badge llamativo con el rango activo, centrado sobre el filtro de fechas */}
      <div className="flex justify-center mb-6">
        <div
          className="inline-flex flex-wrap items-center justify-center gap-2.5 px-6 py-3 rounded-2xl border-2 shadow-sm text-center"
          style={{ backgroundColor: '#EFF6FF', borderColor: '#43BFFF' }}
        >
          <MdCalendarToday className="text-xl flex-shrink-0" style={{ color: '#11194F' }} />
          <span className="text-base font-bold" style={{ color: '#11194F' }}>
            Estás viendo {describirRango(fechaDesde, fechaHasta)} 😉
          </span>
        </div>
      </div>

      {/* Filtros de fecha */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          {/* Fecha desde */}
          <div>
            <label htmlFor="fechaDesde" className="block text-sm font-medium text-gray-700 mb-2">
              Fecha desde: <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="fechaDesde"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              onClick={(e) => {
                const input = e.target as HTMLInputElement;
                if (input.showPicker) {
                  input.showPicker();
                }
              }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
              required
            />
          </div>

          {/* Fecha hasta */}
          <div>
            <label htmlFor="fechaHasta" className="block text-sm font-medium text-gray-700 mb-2">
              Fecha hasta: <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="fechaHasta"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              onClick={(e) => {
                const input = e.target as HTMLInputElement;
                if (input.showPicker) {
                  input.showPicker();
                }
              }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
              required
            />
          </div>


        </div>

        {esRangoPorDefecto(fechaDesde, fechaHasta) && (
          <p className="text-xs text-gray-500 mt-4">
            Inicialmente se muestra el día de hoy.
          </p>
        )}
      </div>

      {/* Header de la sección con acciones */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-bold" style={{ color: '#11194F' }}>
          Boletas - Vouchers
        </h2>
        <div className="flex items-center gap-3">
          {dataUpdatedAt > 0 && (
            <span className="text-xs text-gray-500 hidden sm:inline">
              {formatearAntiguedad(dataUpdatedAt)}
            </span>
          )}
          <button
            onClick={handleRefrescar}
            disabled={refrescarDeshabilitado}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Actualizar boletas"
          >
            <MdRefresh className={`text-xl ${isFetching ? 'animate-spin' : ''}`} style={{ color: '#43BFFF' }} />
            <span className="hidden md:inline">
              {segundosRestantes > 0
                ? `Espera ${segundosRestantes}s`
                : isFetching
                ? 'Actualizando...'
                : 'Actualizar'}
            </span>
          </button>
          <button
            onClick={handleDownloadExcel}
            disabled={!boletas || boletas.length === 0 || isDownloadingExcel}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Exportar a Excel"
          >
            <MdFileDownload className="text-xl text-green-600" />
            <span className="hidden md:inline">{isDownloadingExcel ? 'Exportando...' : 'Exportar'}</span>
          </button>
        </div>
      </div>

      {/* Tabla de resultados */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead style={{ backgroundColor: '#F3F4F9' }}>
              <tr>
                <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                  Voucher
                </th>
                <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                  Boleta SII
                </th>
                <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                  Fecha-Hora
                </th>
                <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                  Vendedor
                </th>
                <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                  Tipo Pago
                </th>
                <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                  Total
                </th>
                <th className="px-6 py-4 text-center text-sm font-bold" style={{ color: '#11194F' }}>
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent" style={{ borderTopColor: '#43BFFF', borderBottomColor: '#43BFFF', borderLeftColor: '#43BFFF' }} />
                    </div>
                    <p className="mt-4 text-gray-600">Cargando boletas...</p>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      {(() => {
                        // Intentar extraer mensajes de validación del error
                        console.log('Error al cargar boletas:', error);
                        const errorObj = error as any;
                        const fieldErrors = errorObj.fieldErrors;
                        
                        if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
                          return (
                            <>
                              <p className="text-red-600 font-semibold">Error de validación</p>
                              {fieldErrors.map((fieldError: any, index: number) => (
                                <p key={index} className="text-red-500">
                                  {fieldError.message}
                                </p>
                              ))}
                            </>
                          );
                        }
                        
                        // Error genérico si no hay fieldErrors
                        return (
                          <p className="text-red-500">
                            Error al cargar las boletas. Por favor, intente nuevamente.
                          </p>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
              ) : !boletas || boletas.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-600">
                    No se encontraron boletas en el rango de fechas seleccionado.
                  </td>
                </tr>
              ) : (
                boletas.map((boleta: Boleta, index) => (
                  <tr
                    key={boleta.id}
                    className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                  >
                    {/* Voucher: mostrar si NO tiene DTE emitido */}
                    <td className="px-6 py-4">
                      <span className="font-semibold" style={{ color: '#11194F' }}>
                        {!boleta.dteEmitido ? boleta.id : '-'}
                      </span>
                    </td>
                    
                    {/* Boleta SII: mostrar si tiene DTE emitido */}
                    <td className="px-6 py-4">
                      <span className="font-semibold" style={{ color: '#11194F' }}>
                        {boleta.dteEmitido && boleta.siiDteEmitidoFolio ? boleta.siiDteEmitidoFolio : '-'}
                      </span>
                    </td>
                    
                    {/* Fecha-Hora */}
                    <td className="px-6 py-4">
                      <span className="text-sm" style={{ color: '#11194F' }}>
                        {formatFechaHora(boleta.fecha)}
                      </span>
                    </td>
                    
                    {/* Vendedor */}
                    <td className="px-6 py-4">
                      <span className="font-semibold" style={{ color: '#11194F' }}>
                        {boleta.vendedor}
                      </span>
                    </td>
                    
                    {/* Tipo Pago */}
                    <td className="px-6 py-4">
                      <span className="font-semibold" style={{ color: '#11194F' }}>
                        {boleta.tipoPago}
                      </span>
                    </td>
                    
                    {/* Total */}
                    <td className="px-6 py-4">
                      <span className="font-bold" style={{ color: '#11194F' }}>
                        ${boleta.total.toLocaleString('es-CL')}
                      </span>
                    </td>
                    
                    {/* Acciones */}
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => printBoleta(boleta.id)}
                          disabled={loadingPrintId !== null}
                          className={`p-2 rounded-lg transition-all ${
                            loadingPrintId === boleta.id
                              ? 'opacity-70 cursor-wait'
                              : 'hover:scale-110 hover:bg-gray-100'
                          }`}
                          style={{ color: '#11194F' }}
                          title={loadingPrintId === boleta.id ? 'Imprimiendo...' : 'Imprimir'}
                        >
                          {loadingPrintId === boleta.id ? (
                            <MdRefresh className="text-xl animate-spin" />
                          ) : (
                            <MdPrint className="text-xl" />
                          )}
                        </button>
                        <button
                          onClick={() => navigate(`/detalle-boleta/${boleta.id}`)}
                          className="p-2 rounded-lg transition-all hover:scale-110"
                          style={{ color: '#43BFFF' }}
                          title="Editar"
                        >
                          <MdEdit className="text-xl" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(boleta)}
                          className="p-2 rounded-lg transition-all hover:scale-110"
                          style={{ color: '#EF4444' }}
                          title="Eliminar"
                        >
                          <MdDelete className="text-xl" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info y totales */}
      {boletas && boletas.length > 0 && (
        <div className="mt-4 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-sm text-gray-600">
            Mostrando {boletas.length} boleta{boletas.length !== 1 ? 's' : ''}
          </div>
          <div className="flex flex-col items-end gap-1 bg-gray-50 px-6 py-3 rounded-lg">
            <p className="text-sm text-gray-700">
              <span className="font-medium">Valor Neto:</span> ${' '}
              {totales.valorNeto.toLocaleString('es-CL')}
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-medium">IVA:</span> ${' '}
              {totales.iva.toLocaleString('es-CL')}
            </p>
            <p className="text-base font-bold text-red-600">
              TOTAL: $ {totales.total.toLocaleString('es-CL')}
            </p>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fadeIn">
            {deleteConfirmation.boleta.dteEmitido ? (
              // Modal cuando tiene DTE emitido
              <>
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100">
                  <MdWarning className="text-4xl text-orange-600" />
                </div>
                
                <h2 className="text-2xl font-bold text-center mb-2" style={{ color: '#11194F' }}>
                  No se puede anular
                </h2>
                
                <p className="text-center text-gray-700 mb-2">
                  Folio #{deleteConfirmation.boleta.dteEmitido && deleteConfirmation.boleta.siiDteEmitidoFolio ? deleteConfirmation.boleta.siiDteEmitidoFolio : deleteConfirmation.boleta.id}
                </p>
                
                <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-6">
                  <p className="text-sm text-orange-800">
                    No se puede anular una venta con Documento Tributario (DTE) emitido. Debe generar una Nota de Crédito.
                  </p>
                </div>
                
                <button
                  onClick={handleCancelDelete}
                  className="w-full px-6 py-3 rounded-xl font-semibold transition-all hover:bg-gray-100 border-2 border-gray-300"
                  style={{ color: '#11194F' }}
                >
                  Entendido
                </button>
              </>
            ) : (
              // Modal de confirmación normal (sin DTE)
              <>
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-red-100">
                  <MdDelete className="text-4xl text-red-600" />
                </div>
                
                <h2 className="text-2xl font-bold text-center mb-2" style={{ color: '#11194F' }}>
                  ¿Eliminar boleta?
                </h2>
                
                <p className="text-center text-gray-600 mb-2">
                  Estás a punto de eliminar la siguiente boleta:
                </p>
                
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p className="text-gray-600">Voucher:</p>
                    <p className="font-semibold" style={{ color: '#11194F' }}>{deleteConfirmation.boleta.id}</p>
                    
                    <p className="text-gray-600">Vendedor:</p>
                    <p className="font-semibold" style={{ color: '#11194F' }}>{deleteConfirmation.boleta.vendedor}</p>
                    
                    <p className="text-gray-600">Total:</p>
                    <p className="font-semibold" style={{ color: '#11194F' }}>${deleteConfirmation.boleta.total.toLocaleString('es-CL')}</p>
                  </div>
                </div>
                
                <p className="text-center text-sm text-gray-500 mb-6">
                  Esta acción no se puede deshacer.
                </p>
                
                <div className="flex gap-3">
                  <button
                    onClick={handleCancelDelete}
                    disabled={eliminarBoletaMutation.isPending}
                    className="flex-1 px-6 py-3 rounded-xl font-semibold transition-all hover:bg-gray-100 border-2 border-gray-300 disabled:opacity-50"
                    style={{ color: '#11194F' }}
                  >
                    Cancelar
                  </button>
                  
                  <button
                    onClick={handleConfirmDelete}
                    disabled={eliminarBoletaMutation.isPending}
                    className="flex-1 px-6 py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ backgroundColor: '#EF4444' }}
                  >
                    {eliminarBoletaMutation.isPending ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                        <span>Eliminando...</span>
                      </>
                    ) : (
                      'Sí, eliminar'
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
